import {KeyboardEvent, MutableRefObject} from 'react';

import {RichTextProps} from '../types';
import {executeToolbarCommand} from '../utils/editorCommands';
import {getSelectedFormat, HEADING_ELEMENTS} from '../utils/selectionHelpers';

const TOOLBAR_NAVIGATION_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'Home', 'End']);

export function useEditorKeyboard(
  props: RichTextProps,
  editorRef: MutableRefObject<HTMLDivElement | null>,
  toolbarRef: MutableRefObject<HTMLDivElement | null>,
  selectionRef: MutableRefObject<Range | null>,
  syncEditorState: () => void,
  updateMetadata: () => void
) {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if ('Enter' === event.key && !event.shiftKey) {
      const node = editorRef.current;

      if (node) {
        const currentFormat = getSelectedFormat(node);

        if (HEADING_ELEMENTS.has(currentFormat)) {
          setTimeout(() => {
            document.execCommand('formatBlock', false, '<p>');
            syncEditorState();
            updateMetadata();
          }, 0);
        }
      }
    }

    const isModifierPressed = event.ctrlKey || event.metaKey;

    if (isModifierPressed && 'b' === event.key.toLowerCase()) {
      event.preventDefault();
      executeToolbarCommand(editorRef.current, selectionRef, 'bold');
      syncEditorState();
      updateMetadata();
    }

    if (isModifierPressed && 'i' === event.key.toLowerCase()) {
      event.preventDefault();
      executeToolbarCommand(editorRef.current, selectionRef, 'italic');
      syncEditorState();
      updateMetadata();
    }

    if (isModifierPressed && 'u' === event.key.toLowerCase()) {
      event.preventDefault();
      executeToolbarCommand(editorRef.current, selectionRef, 'underline');
      syncEditorState();
      updateMetadata();
    }

    props.onKeyDown?.(event);
  };

  const handleToolbarKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!toolbarRef.current || !(event.target instanceof HTMLElement)) {
      return;
    }

    if (!TOOLBAR_NAVIGATION_KEYS.has(event.key)) {
      return;
    }

    const items = Array.from(
      toolbarRef.current.querySelectorAll<HTMLElement>(
        '[data-rich-text-toolbar-item="true"]:not(:disabled)'
      )
    );
    const currentIndex = items.findIndex((item) => item === event.target);

    if (-1 === currentIndex) {
      return;
    }

    event.preventDefault();

    if ('Home' === event.key) {
      items[0]?.focus();
      return;
    }

    if ('End' === event.key) {
      items[items.length - 1]?.focus();
      return;
    }

    const direction = 'ArrowRight' === event.key ? 1 : -1;
    const nextIndex = (currentIndex + direction + items.length) % items.length;
    items[nextIndex]?.focus();
  };

  return {
    handleKeyDown,
    handleToolbarKeyDown,
  };
}
